import React, { ChangeEventHandler, ReactElement, useRef } from 'react';
import { FileUploadStatus, MimeType, MimeTypeExtensionMapping } from './constants';
import { truncateFileNameKeepingExtension } from 'utils/FileName';
import { getStaticMediaUrl } from 'utils/Urls';
import fileUploadIcon from 'assets/file--upload--not--initiated.svg';
import fileUploadSuccess from 'assets/file--upload--success.svg';
import fileUploadFailure from 'assets/file--upload--failure.svg';
import {
  Box,
  CheckCircleIcon,
  CloseIcon,
  IconButton,
  Link,
  ProgressBar,
  RotateClockWiseIcon,
  Text,
} from '@razorpay/blade/components';
import { FileUploadAreaWrapper } from './styles';

const getFileConstraintsText = (allowedMimeTypes: MimeType[], maxSize: number) => {
  let allowedExtensions: String[] = [];

  allowedMimeTypes.map((mimeType) => allowedExtensions.push(...MimeTypeExtensionMapping(mimeType)));

  let retVal = allowedExtensions.join(', ') + ' file formats allowed';

  retVal = retVal + ' | Max Size ' + maxSize + ' MB';

  return retVal;
};

export const BulkFileUpload = ({
  id,
  onChange,
  onError,
  onCancel,
  status,
  allowedMimeTypes,
  maxSize = 5,
  value,
  error,
  disableFileReuploadButton,
  disableCancelButton,
}: {
  id: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onError?: () => {};
  onCancel?: () => void;
  status: number;
  allowedMimeTypes: MimeType[];
  maxSize: number; //in MB,
  value?: string;
  error?: string;
  disableFileReuploadButton?: boolean;
  disableCancelButton?: boolean;
}): ReactElement => {
  const inputFileRef = useRef<HTMLInputElement>(null);

  const onClick = () => {
    if (status !== FileUploadStatus.UPLOADING) {
      inputFileRef.current?.click();
    }
  };

  const getIconBasedOnStatus = (status: FileUploadStatus) => {
    switch (status) {
      case FileUploadStatus.NOT_INITIATED:
        return null;

      case FileUploadStatus.UPLOADING:
        return <img width="auto" height="38px" alt="" src={getStaticMediaUrl(fileUploadIcon)} />;

      case FileUploadStatus.SUCCESS:
        return (
          <img alt="" width="auto" height="38px" src={getStaticMediaUrl(fileUploadSuccess)}></img>
        );

      case FileUploadStatus.FAILURE:
        return (
          <img alt="" width="auto" height="38px" src={getStaticMediaUrl(fileUploadFailure)}></img>
        );
    }
  };

  const getStatusTextBasedOnStatus = (status: FileUploadStatus) => {
    switch (status) {
      case FileUploadStatus.UPLOADING:
        return (
          <Text size="small" weight="regular" color="surface.text.gray.muted">
            (Validating)
          </Text>
        );

      case FileUploadStatus.FAILURE: {
        return (
          <Text
            size="small"
            variant="caption"
            weight="regular"
            color="feedback.text.negative.intense">
            {!error
              ? 'Processing failed'
              : error === ''
              ? 'Processing failed, please re-upload valid file.'
              : error}
          </Text>
        );
      }

      default:
        return null;
    }
  };

  const getReplaceFileLabel = (status: FileUploadStatus) => {
    if (status === FileUploadStatus.SUCCESS && !disableFileReuploadButton) {
      return (
        <Link
          size="small"
          color="neutral"
          variant="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            inputFileRef.current?.click();
          }}>
          Replace
        </Link>
      );
    }
    if (status === FileUploadStatus.FAILURE && !disableFileReuploadButton) {
      return (
        <Link
          size="small"
          color="negative"
          variant="button"
          icon={RotateClockWiseIcon}
          iconPosition="left"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            inputFileRef.current?.click();
          }}>
          Re-upload
        </Link>
      );
    }

    if (status === FileUploadStatus.UPLOADING && !disableCancelButton && onCancel) {
      return (
        <IconButton
          size="small"
          icon={CloseIcon}
          onClick={onCancel}
          accessibilityLabel="Cancel upload"
        />
      );
    }
    return null;
  };

  return (
    <Box>
      <FileUploadAreaWrapper status={status} onClick={onClick}>
        <Box
          gap={'spacing.4'}
          display="flex"
          height="100%"
          width="100%"
          flexGrow={1}
          minHeight="54px"
          padding="spacing.3">
          {status !== FileUploadStatus.UPLOADING && (
            <input
              aria-label="Select File"
              type="file"
              id={id}
              name={id}
              onChange={(event) => {
                onChange(event);
                event.target.value = '';
              }}
              className="inputfile invisible"
              ref={inputFileRef}
            />
          )}

          <Box flexGrow={1}>
            {status === FileUploadStatus.NOT_INITIATED ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                width="100%">
                <label
                  htmlFor={id}
                  onClick={(event) => {
                    event.preventDefault();
                  }}
                  className="cursor-pointer">
                  <Text color="surface.text.gray.subtle">Browse files to upload</Text>
                </label>
              </Box>
            ) : value ? (
              <Box display="flex" gap="spacing.4">
                {getIconBasedOnStatus(status)}
                <Box display="flex" flexDirection="column" gap="spacing.2" justifyContent="center">
                  <Box display="flex" gap="spacing.2" alignItems="center">
                    <Text color="surface.text.gray.subtle">
                      {truncateFileNameKeepingExtension(value, 25)}
                    </Text>
                    {status === FileUploadStatus.SUCCESS && (
                      <CheckCircleIcon color="interactive.icon.positive.normal" size="small" />
                    )}
                  </Box>
                  {getStatusTextBasedOnStatus(status)}
                </Box>
              </Box>
            ) : (
              <Text color="surface.text.gray.subtle">No file selected</Text>
            )}
          </Box>
          <Box marginLeft="auto" marginTop="spacing.4">
            {getReplaceFileLabel(status)}
          </Box>
        </Box>
        {status === FileUploadStatus.UPLOADING && (
          <ProgressBar
            position="relative"
            top="0px"
            variant="linear"
            isIndeterminate={true}
            showPercentage={false}
          />
        )}
      </FileUploadAreaWrapper>

      <Text
        marginTop="spacing.3"
        size="small"
        weight="regular"
        variant="caption"
        color="surface.text.gray.muted">
        {getFileConstraintsText(allowedMimeTypes, maxSize)}
      </Text>
    </Box>
  );
};
